import { useEffect, useReducer, useState, type ReactNode } from 'react'
import { useFlow } from '../flow/FlowContext'
import { useSession } from '../session/SessionContext'
import { useCustomer, useValidate } from '../api/queries'
import { usePosContext } from '../lightspeed/usePosContext'
import { getLightspeedApi, isPosHost } from '../lightspeed/pos-api'
import { config } from '../config/env'
import { describeCredential, readPosCredential } from '../config/credentials'
import { playSoundDebug, type SoundName } from '../sound'
import {
  collectWebViewFacts,
  formatFacts,
  probeBothRoutes,
  type CorsProbeResult,
} from '../debug/webview-origin'
import {
  clearDebugLog,
  getDebugLog,
  subscribeDebugLog,
  type DebugLogEntry,
} from '../debug/errorLog'

const SOUND_NAMES: SoundName[] = ['scan', 'reward', 'complete']

function Json({ value }: { value: unknown }) {
  return (
    <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-black/40 p-2 text-[10px] leading-tight text-brand-200">
      {value === undefined ? '—' : JSON.stringify(value, null, 2)}
    </pre>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details open className="border-t border-white/10 py-1">
      <summary className="cursor-pointer select-none text-[11px] font-semibold uppercase tracking-wide text-white/70">
        {title}
      </summary>
      <div className="mt-1 space-y-1">{children}</div>
    </details>
  )
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex gap-2 text-[11px]">
      <span className="shrink-0 text-white/50">{k}</span>
      <span className="break-all text-white/90">{v}</span>
    </div>
  )
}

/** Colour the probe by whether the till WORKS, not by whether CORS let the answer through.
 *
 * `allowed` only means the browser did not block the response. A 401 is `allowed` — the
 * request arrived and the surface answered — and colouring that green reported a broken
 * till as healthy. Someone reading this panel on an iPad, looking for why the app will
 * not load, saw a green line and moved on. Amber, because it is genuinely a different
 * failure from a blocked or unreachable surface: the transport is fine and the credential
 * is the missing piece. */
function probeTone(probe: CorsProbeResult): string {
  if (probe.verdict !== 'allowed') return 'text-red-300'
  if (probe.status === 401) return 'text-amber-300'
  return 'text-emerald-300'
}

/** On-demand POS data dumps (DEV-67). Grouped in a reducer so the panel keeps a small set of
 * state slices instead of many independent useState calls. */
interface DumpState {
  account?: unknown
  rawAccount?: unknown
  discounts?: unknown
  error: string | null
}
type DumpAction =
  | { type: 'account'; value: unknown }
  | { type: 'rawAccount'; value: unknown }
  | { type: 'discounts'; value: unknown }
  | { type: 'error'; message: string }

function dumpReducer(state: DumpState, action: DumpAction): DumpState {
  switch (action.type) {
    case 'account':
      return { ...state, account: action.value, error: null }
    case 'rawAccount':
      return { ...state, rawAccount: action.value, error: null }
    case 'discounts':
      return { ...state, discounts: action.value, error: null }
    case 'error':
      return { ...state, error: action.message }
  }
}

/** Force a fresh load: drop any service workers + Cache Storage, then reload with a cache-busting
 * param (keeping webext_uuid/debug) so the POS web view can't serve a stale index.html. Pure
 * (browser globals only), so it lives at module scope rather than being rebuilt every render. */
async function forceReload() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // Best effort — reload regardless.
  }
  const url = new URL(window.location.href)
  url.searchParams.set('_cb', String(Date.now()))
  window.location.replace(url.toString())
}

/** On-screen debug overlay (gated by isDebugEnabled). Renders nothing for cashiers; for the
 * operator it surfaces config, POS host state, the live query data, on-demand POS dumps, and
 * captured runtime errors. */
export function DebugPanel() {
  const [open, setOpen] = useState(false)
  const { state } = useFlow()
  const { scannedQr } = useSession()
  const validate = useValidate()
  const customer = useCustomer(scannedQr)
  const posContext = usePosContext()

  const [version, setVersion] = useState<string | null>(null)
  const [dumps, dispatchDump] = useReducer(dumpReducer, {
    account: undefined,
    rawAccount: undefined,
    discounts: undefined,
    error: null,
  })

  const [log, setLog] = useState<DebugLogEntry[]>(() => getDebugLog())
  useEffect(() => subscribeDebugLog(() => setLog(getDebugLog())), [])

  // Manual sound check — tapping a button is itself the user gesture iOS requires, so this
  // isolates the codec question from the autoplay-on-scan question. Shows ✓ or the error reason.
  const [soundResult, setSoundResult] = useState<string | null>(null)
  async function testSound(name: SoundName) {
    const err = await playSoundDebug(name)
    setSoundResult(err ? `${name}: ✗ ${err}` : `${name}: ✓ played`)
  }

  // Open the panel and fetch the POS app version the first time — done in the handler, not an
  // effect, so we don't chain a state update off the panel's own `open` state.
  function openPanel() {
    setOpen(true)
    if (version !== null) return
    if (typeof pos_getVersion !== 'function') {
      setVersion('n/a (no host)')
      return
    }
    pos_getVersion((r) => setVersion(r?.data ?? `error: ${r?.error?.message ?? 'unknown'}`))
  }

  async function dump(what: 'account' | 'discounts') {
    try {
      const ls = getLightspeedApi()
      if (what === 'account') dispatchDump({ type: 'account', value: await ls.getCurrentAccount() })
      else dispatchDump({ type: 'discounts', value: await ls.getAvailableDiscounts() })
    } catch (e) {
      dispatchDump({ type: 'error', message: e instanceof Error ? e.message : String(e) })
    }
  }

  // RAW account straight from pos_getCurrentAccount, BEFORE our normalize — so we can see the true
  // device field names (e.g. whether externalReferences ever appear, and under what key). Scan a
  // customer first, then dump: if externalReferences is still [], the host doesn't echo the refs we
  // add (confirming why auto-relink can't read them off the account).
  // DEV-378. What the K-Series web view sends as its `Origin`, read off real hardware
  // rather than assumed. Collected on every render because it is three property reads
  // and it must never be stale when someone is copying it into a config.
  const webViewFacts = collectWebViewFacts(config.posSurfaceUrl)
  const [corsProbe, setCorsProbe] = useState<CorsProbeResult | null>(null)
  const [absoluteProbe, setAbsoluteProbe] = useState<CorsProbeResult | null>(null)
  const [copied, setCopied] = useState(false)

  // Both routes in one tap. Same-origin is the one that can carry the page's Basic
  // credentials; the absolute URL is the CORS path. Which of them answers is the whole
  // question, and a single trip to the iPad should settle it.
  const ABSOLUTE_POS_URL =
    'https://europe-west3-badgerreal-14a04.cloudfunctions.net/badgerv2_pos'

  async function runCorsProbe() {
    setCorsProbe(null)
    setAbsoluteProbe(null)
    const result = await probeBothRoutes(config.posSurfaceUrl, ABSOLUTE_POS_URL)
    setCorsProbe(result.sameOrigin)
    setAbsoluteProbe(result.absolute)
  }

  async function copyFacts() {
    try {
      await navigator.clipboard.writeText(formatFacts(webViewFacts, corsProbe ?? undefined))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard is gesture-gated and can be unavailable in a sandboxed web view. The
      // values are on screen either way; this is a convenience, not the delivery.
      setCopied(false)
    }
  }

  function dumpRawAccount() {
    if (typeof pos_getCurrentAccount !== 'function') {
      dispatchDump({ type: 'error', message: 'no host (pos_getCurrentAccount unavailable)' })
      return
    }
    pos_getCurrentAccount((r) => {
      if (r?.error) dispatchDump({ type: 'error', message: r.error.message })
      else dispatchDump({ type: 'rawAccount', value: r?.data })
    })
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={openPanel}
        className="fixed bottom-2 right-2 z-50 rounded-full bg-slate-900/90 px-3 py-1 font-mono text-[11px] text-brand-300 shadow-lg"
      >
        🐞 {state.step}
        {state.error ? ' ⚠' : ''}
      </button>
    )
  }

  return (
    <div className="fixed inset-x-2 bottom-2 z-50 max-h-[80vh] overflow-auto rounded-xl bg-slate-900/95 p-3 font-mono text-white shadow-2xl">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold text-brand-300">Badger debug</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={forceReload}
            className="rounded bg-brand-500 px-2 py-0.5 text-[11px] font-semibold"
          >
            ⟳ reload + clear cache
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded bg-white/10 px-2 py-0.5 text-[11px]"
          >
            close
          </button>
        </div>
      </div>

      <Section title="Config / env">
        <Row k="mode" v={import.meta.env.MODE} />
        <Row k="host" v={window.location.host} />
        <Row k="backend (v1)" v={config.backendBaseUrl || '(unset)'} />
        <Row k="pos surface (v2)" v={config.posSurfaceUrl || '(unset)'} />
        <Row k="webext_uuid (v1)" v={config.webextUuid || '(unset)'} />
        {/* Username only. A password on a debug screen is a password on a photo of a
         * debug screen. */}
        <Row k="pos credential (v2)" v={describeCredential(readPosCredential())} />
        <Row k="pos host" v={String(isPosHost())} />
        <Row k="pos version" v={version ?? '(open to fetch)'} />
      </Section>

      <Section title="Web view origin + CORS (DEV-378)">
        {/* `origin` is the exact string the browser puts in the Origin header, so it is
         * the value that goes into POS_ALLOWED_ORIGINS. Everything else explains why. */}
        <Row
          k="origin"
          v={
            <span className={webViewFacts.opaqueOrigin ? 'text-red-300' : 'text-emerald-300'}>
              {webViewFacts.origin || '(empty)'}
            </span>
          }
        />
        {webViewFacts.opaqueOrigin ? (
          <Row
            k="⚠"
            v="opaque origin — an allow-list can never match this. The surface would need a different approach."
          />
        ) : null}
        <Row k="in iframe" v={String(webViewFacts.inIframe)} />
        <Row k="href" v={webViewFacts.href} />
        <Row k="referrer" v={webViewFacts.referrer || '(none)'} />
        <Row k="secure ctx" v={String(webViewFacts.secureContext)} />
        <Row k="user agent" v={webViewFacts.userAgent} />

        <div className="mt-1 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={runCorsProbe}
            className="rounded bg-brand-500 px-2 py-0.5 text-[11px] font-semibold"
          >
            probe POS CORS
          </button>
          <button
            type="button"
            onClick={copyFacts}
            className="rounded bg-white/10 px-2 py-0.5 text-[11px]"
          >
            {copied ? '✓ copied' : 'copy all'}
          </button>
        </div>

        {corsProbe ? (
          <>
            <Row k="— same-origin —" v={config.posSurfaceUrl} />
            <Row
              k="verdict"
              v={
                <span className={probeTone(corsProbe)}>
                  {corsProbe.verdict}
                  {corsProbe.status ? ` (HTTP ${corsProbe.status})` : ''}
                </span>
              }
            />
            <Row k="detail" v={corsProbe.detail} />
          </>
        ) : null}
        {absoluteProbe ? (
          <>
            <Row k="— cross-origin —" v="cloudfunctions.net" />
            <Row
              k="verdict"
              v={
                <span className={probeTone(absoluteProbe)}>
                  {absoluteProbe.verdict}
                  {absoluteProbe.status ? ` (HTTP ${absoluteProbe.status})` : ''}
                </span>
              }
            />
            <Row k="detail" v={absoluteProbe.detail} />
          </>
        ) : null}
      </Section>

      <Section title="Flow">
        <Row k="step" v={state.step} />
        <Row k="error" v={state.error ?? '—'} />
        {/* The identifier support actually needs. The message is translated prose that
         * changes with the locale; this does not. */}
        <Row
          k="error code"
          v={
            state.errorCode ? (
              <span className="select-all text-amber-300">{state.errorCode}</span>
            ) : (
              '—'
            )
          }
        />
        <Row k="scanned qr" v={scannedQr ?? '—'} />
      </Section>

      <Section title="POS context">
        <Json value={posContext} />
      </Section>

      <Section title="/validate">
        <Row k="status" v={`${validate.status}${validate.isFetching ? ' (fetching)' : ''}`} />
        <Json value={validate.data} />
      </Section>

      <Section title="/customer">
        <Row k="status" v={`${customer.status}${customer.isFetching ? ' (fetching)' : ''}`} />
        <Json value={customer.data} />
      </Section>

      <Section title="Sounds">
        <div className="flex flex-wrap gap-2">
          {SOUND_NAMES.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => void testSound(name)}
              className="rounded bg-brand-600 px-2 py-1 text-[11px]"
            >
              ▶ {name}
            </button>
          ))}
        </div>
        <Row k="result" v={soundResult ?? '—'} />
      </Section>

      <Section title="POS dumps (DEV-67)">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => dump('account')}
            className="rounded bg-brand-600 px-2 py-1 text-[11px]"
          >
            Dump account
          </button>
          <button
            type="button"
            onClick={() => dump('discounts')}
            className="rounded bg-brand-600 px-2 py-1 text-[11px]"
          >
            Dump discounts
          </button>
          <button
            type="button"
            onClick={dumpRawAccount}
            className="rounded bg-amber-600 px-2 py-1 text-[11px]"
          >
            Dump RAW account
          </button>
        </div>
        {dumps.error && <div className="text-[11px] text-red-400">{dumps.error}</div>}
        <Row k="account (normalized)" v="" />
        <Json value={dumps.account} />
        <Row k="account (RAW)" v="" />
        <Json value={dumps.rawAccount} />
        <Row k="discounts" v="" />
        <Json value={dumps.discounts} />
      </Section>

      <Section title={`Errors (${log.length})`}>
        <button
          type="button"
          onClick={clearDebugLog}
          className="rounded bg-white/10 px-2 py-0.5 text-[11px]"
        >
          clear
        </button>
        {log.length === 0 && <div className="text-[11px] text-white/50">none</div>}
        {log.map((e) => (
          <div key={e.id} className="rounded bg-black/40 p-2 text-[10px]">
            <div className="text-red-300">
              [{e.time}] {e.kind}: {e.message}
            </div>
            {e.stack && (
              <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-all text-white/60">
                {e.stack}
              </pre>
            )}
          </div>
        ))}
      </Section>
    </div>
  )
}
